import { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Alert, Row, Col } from "react-bootstrap";
import Api from "../../../Services/Api";

interface TreatmentPeriodicity {
  id: number;
  timeInterval: number;
  timeIntervalUnitId: number;
  timeIntervalUnit?: {
    id: number;
    name: string;
    hoursDuration: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

const TreatmentPeriodicities = () => {
  const [periodicities, setPeriodicities] = useState<TreatmentPeriodicity[]>([]);
  const [timeIntervalUnits, setTimeIntervalUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPeriodicity, setEditingPeriodicity] = useState<TreatmentPeriodicity | null>(null);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  
  const [formData, setFormData] = useState({
    timeInterval: 0,
    timeIntervalUnitId: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const api = new Api();
    const [periodicitiesData, unitsData] = await Promise.all([
      api.getTreatmentPeriodicities(),
      api.getTimeIntervalUnits(),
    ]);
    if (periodicitiesData) {
      setPeriodicities(periodicitiesData);
    }
    if (unitsData) {
      setTimeIntervalUnits(unitsData);
    }
    setLoading(false);
  };

  const handleOpenCreate = () => {
    setEditingPeriodicity(null);
    setFormData({ timeInterval: 0, timeIntervalUnitId: 0 });
    setError("");
    setSuccess("");
    setShowModal(true);
  };

  const handleOpenEdit = (periodicity: TreatmentPeriodicity) => {
    setEditingPeriodicity(periodicity);
    setFormData({
      timeInterval: periodicity.timeInterval,
      timeIntervalUnitId: periodicity.timeIntervalUnitId,
    });
    setError("");
    setSuccess("");
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingPeriodicity(null);
    setFormData({ timeInterval: 0, timeIntervalUnitId: 0 });
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.timeInterval || formData.timeInterval <= 0 || !formData.timeIntervalUnitId) {
      setError("Please fill in all fields with valid values");
      return;
    }

    try {
      const api = new Api();
      if (editingPeriodicity) {
        await api.updateTreatmentPeriodicity(editingPeriodicity.id, formData);
        setSuccess("Treatment periodicity updated successfully");
      } else {
        await api.createTreatmentPeriodicity(formData);
        setSuccess("Treatment periodicity created successfully");
      }
      handleClose();
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this treatment periodicity?")) {
      return;
    }

    try {
      const api = new Api();
      await api.deleteTreatmentPeriodicity(id);
      setSuccess("Treatment periodicity deleted successfully");
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Row className="mb-3">
        <Col>
          <h3>Treatment Periodicities</h3>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleOpenCreate}>
            Create New
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible>{success}</Alert>}

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Time Interval</th>
            <th>Time Interval Unit</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {periodicities.map((periodicity) => (
            <tr key={periodicity.id}>
              <td>{periodicity.id}</td>
              <td>{periodicity.timeInterval}</td>
              <td>
                {periodicity.timeIntervalUnit
                  ? `${periodicity.timeIntervalUnit.name} (${periodicity.timeIntervalUnit.hoursDuration}h)`
                  : `Unit ID: ${periodicity.timeIntervalUnitId}`}
              </td>
              <td>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleOpenEdit(periodicity)}
                  className="me-2"
                >
                  Edit
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDelete(periodicity.id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingPeriodicity ? "Edit Treatment Periodicity" : "Create Treatment Periodicity"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Time Interval</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={formData.timeInterval}
                onChange={(e) => setFormData({ ...formData, timeInterval: parseInt(e.target.value) || 0 })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Time Interval Unit</Form.Label>
              <Form.Select
                value={formData.timeIntervalUnitId}
                onChange={(e) => setFormData({ ...formData, timeIntervalUnitId: parseInt(e.target.value) })}
                required
              >
                <option value={0}>Select a unit</option>
                {timeIntervalUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name} ({unit.hoursDuration}h)
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingPeriodicity ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default TreatmentPeriodicities;
